import { useEffect, useRef } from 'react';

// Calls onResume() once per tab-return, coalescing the visibilitychange + focus
// events (both fire when a backgrounded/locked tab becomes active again) into a
// single invocation via a microtask. Used to resync schedule state after sleep.
//
// The latest onResume is held in a ref so the effect can keep `[]` deps and not
// re-subscribe its listeners on every render, regardless of callback identity.
const useResumeSignal = onResume => {
  const onResumeRef = useRef(onResume);
  onResumeRef.current = onResume;

  const resumePending = useRef(false);

  useEffect(() => {
    const handleResume = () => {
      if (document.visibilityState !== 'visible') return;
      if (resumePending.current) return;
      resumePending.current = true;
      Promise.resolve().then(() => {
        resumePending.current = false;
        onResumeRef.current();
      });
    };

    document.addEventListener('visibilitychange', handleResume);
    window.addEventListener('focus', handleResume);

    return () => {
      document.removeEventListener('visibilitychange', handleResume);
      window.removeEventListener('focus', handleResume);
    };
  }, []);
};

export default useResumeSignal;
